import React from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import { Fade, Tooltip } from "@mui/material";
import { ModelResponseDto } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import tailwindClassnames from "../common/TailwindClassnames";
import { InfoIcon } from "../common/ProjectIcons";

type Props = {
  thresholdInputId: string;
  models: ModelResponseDto[];
  modelsLoading: boolean;
  selectedModel: ModelResponseDto | null;
  onSelectModel: (model: ModelResponseDto | null) => void;
  confThreshError: string;
  onConfThreshChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  hint: string;
  /**
   * Disables the model select + threshold input because there is nothing to
   * choose from yet (the model list is still loading). The threshold input is
   * additionally disabled until a model is selected — there is no model to
   * carry the value, and editing it in that state used to fabricate a
   * model-less object that made the run button think it was configured.
   *
   * A run already being in progress deliberately does **not** set this: the
   * user can keep picking a model and adjusting the threshold while one runs,
   * ready for the next run. Only the run button locks out in that case, via
   * `runDisabled`.
   */
  disabled: boolean;
  isRunning: boolean;
  runDisabled: boolean;
  runLabel: string;
  onRun: () => void;
  /**
   * Why the run button is blocked — e.g. "an import or upload is already in
   * progress for this selection". Surfaced as a hover/focus tooltip on the
   * disabled button rather than as standing text under the row, so it only
   * appears when the user goes looking for it.
   */
  blockReason?: string;
};

/**
 * Shared model + confidence-threshold + run-button row used by both the
 * Detection and Classification stage cards (AKL-766) — one set of controls
 * whose scope (a single deployment or the whole project's "All deployments")
 * is decided by the caller via runLabel/runDisabled/onRun, not by this
 * component.
 */
const InferenceStagePanel: React.FC<Props> = ({
  thresholdInputId,
  models,
  modelsLoading,
  selectedModel,
  onSelectModel,
  confThreshError,
  onConfThreshChange,
  hint,
  disabled,
  isRunning,
  runDisabled,
  runLabel,
  onRun,
  blockReason,
}) => {
  const { t } = useTranslation();

  const confThreshTooltip = (
    <Tooltip
      title={t("Leave blank to use the model's default confidence threshold")}
      placement="top"
      arrow
      slotProps={{ tooltip: { sx: { fontSize: "1em" } } }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
    >
      <span tabIndex={0} className="cursor-pointer flex items-center">
        {/* Sized down from the 26px default and stripped of its `pb-2.5`, so
            the tooltip does not make this label row taller than the Model
            select's label beside it — the two controls below have to start at
            the same y. */}
        <InfoIcon
          size={18}
          className="text-off-black-light dark:text-storm-blue-light transition duration-300"
        />
      </span>
    </Tooltip>
  );

  return (
    <div>
      <div className="flex flex-wrap gap-4 items-start">
        <div className="flex-1 min-w-[280px] max-w-md">
          <SelectInput
            options={models.map((model) => ({
              value: model,
              label: model.name,
            }))}
            className="w-full bg-storm-blue rounded h-full shadow text-left text-sm"
            isDisabled={disabled}
            isLoading={modelsLoading}
            isClearable={true}
            isSearchable={true}
            value={
              selectedModel === null
                ? null
                : { value: selectedModel, label: selectedModel.name }
            }
            isMulti={false}
            name={t("Model")}
            onChange={(e: any) => onSelectModel(e ? e.value : null)}
            errors=""
            searchBar={false}
          />
        </div>
        {/* No `gap` on this column: the label already carries the same `mb-1`
            the Model select's label does, so the threshold input and the select
            start at the same height. Spacing below is applied per element. */}
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <label
              htmlFor={thresholdInputId}
              className={tailwindClassnames.textInputLabel}
            >
              {t("Confidence threshold (0–100)")}
            </label>
            {confThreshTooltip}
          </div>
          {/* Column gap is 0 on purpose: `saveButton`/`disabledSaveButton`
              already carry `ml-4`, so a `gap-x` here stacked on top of it and
              left the run button drifting away from the input. `gap-y` still
              spaces the rows when this wraps. */}
          <div className="flex items-center gap-y-2 flex-wrap">
            <div className="relative">
              <input
                id={thresholdInputId}
                name="default_confidence_threshold"
                type="number"
                min={0}
                max={100}
                step={1}
                value={selectedModel?.default_confidence_threshold ?? ""}
                disabled={disabled || !selectedModel}
                onChange={onConfThreshChange}
                aria-describedby={`${thresholdInputId}-hint`}
                className="w-[84px] h-11 rounded pl-3 pr-7 text-primary-off-black dark:text-off-white-dark bg-white border border-off-white-dark dark:border-off-black-light dark:bg-storm-blue placeholder-off-black-light transition duration-300 focus:outline-none focus:ring-2 focus:ring-storm-blue dark:focus:ring-sky-blue focus:border-sky-blue-dark disabled:bg-off-white-dark disabled:text-off-black-light dark:disabled:bg-storm-blue-light"
              />
              <span
                aria-hidden="true"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-greyed-text"
              >
                %
              </span>
            </div>
            {isRunning ? (
              <div className="place-content-center ml-2 px-2">
                <BeatLoader
                  color={tailwindClassnames.skyBlueDark}
                  loading={true}
                  size={10}
                  margin={5}
                />
              </div>
            ) : (
              <Tooltip
                title={runDisabled && blockReason ? blockReason : ""}
                placement="top"
                arrow
                slotProps={{ tooltip: { sx: { fontSize: "1em" } } }}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 600 }}
              >
                {/* A disabled <button> fires no mouse events, so MUI needs a
                    non-disabled wrapper or the tooltip never opens. The span
                    takes focus while the button is blocked so the reason is
                    reachable by keyboard as well as by hover. */}
                <span
                  className="inline-flex"
                  tabIndex={runDisabled && blockReason ? 0 : undefined}
                >
                  <button
                    type="button"
                    className={
                      runDisabled
                        ? tailwindClassnames.disabledSaveButton
                        : tailwindClassnames.saveButton
                    }
                    disabled={runDisabled}
                    onClick={onRun}
                  >
                    {runLabel}
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
          <p
            id={`${thresholdInputId}-hint`}
            className="mt-1.5 text-xs text-off-black-light dark:text-greyed-text max-w-[420px]"
          >
            {hint}
          </p>
          {confThreshError && (
            <p role="alert" className="mt-1.5 text-xs text-primary-orange-dark">
              {confThreshError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InferenceStagePanel;
